import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Asset } from '@/types/models';
import { resolveAssetByBarcode } from '@/services/assetService';
import { AssetQuickViewDrawer } from '@/components/assets/AssetQuickViewDrawer';
import { Loader2, AlertTriangle, ScanLine, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

type ResolveState = 'loading' | 'found' | 'not_found' | 'invalid' | 'error';

export default function AssetResolverPage() {
  const { assetUid } = useParams<{ assetUid: string }>();
  const [state, setState] = useState<ResolveState>('loading');
  const [asset, setAsset] = useState<Asset | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!assetUid) {
      setState('invalid');
      return;
    }

    setState('loading');
    resolveAssetByBarcode(assetUid)
      .then((result) => {
        if (result) {
          setAsset(result);
          setState('found');
          setDrawerOpen(true);
        } else {
          setState('not_found');
        }
      })
      .catch(() => {
        setState('error');
      });
  }, [assetUid]);

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      {state === 'loading' && (
        <div className="flex flex-col items-center gap-4 text-muted-foreground">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="text-lg font-medium">Resolving asset…</p>
          <p className="text-sm font-mono">{assetUid}</p>
        </div>
      )}

      {state === 'not_found' && (
        <div className="flex flex-col items-center gap-4 text-center max-w-sm">
          <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <ScanLine className="h-8 w-8 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Asset Not Found</h2>
          <p className="text-muted-foreground">
            No asset matches <span className="font-mono font-medium text-foreground">{assetUid}</span>. The QR code may be outdated or the asset hasn't been registered yet.
          </p>
          <Button variant="outline" onClick={() => window.history.back()}>
            Go Back
          </Button>
        </div>
      )}

      {state === 'invalid' && (
        <div className="flex flex-col items-center gap-4 text-center max-w-sm">
          <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Invalid QR Code</h2>
          <p className="text-muted-foreground">The scanned code does not contain a valid asset identifier.</p>
          <Button variant="outline" onClick={() => window.history.back()}>
            Go Back
          </Button>
        </div>
      )}

      {state === 'error' && (
        <div className="flex flex-col items-center gap-4 text-center max-w-sm">
          <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <WifiOff className="h-8 w-8 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">Network Error</h2>
          <p className="text-muted-foreground">Could not reach the server. Check your connection and try again.</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      )}

      <AssetQuickViewDrawer
        asset={asset}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />
    </div>
  );
}
